import { type PlaceDetails, placesAutocomplete, placesDetails } from "@/api/places-client";
import {
  type StepBuildCeremony,
  mergeStepBuildProps,
  pageBodyBlockBuildProps,
  sectionPageIntroMotions,
} from "@/components/benefits-discovery-v2/step-sequential-build";
import { ArrowRight, Briefcase, Hash, Mail, Phone, Store, User } from "@cakewalk/ui/icons";
import {
  V2AddressAutocomplete,
  type V2AddressSuggestion,
  type V2AddressValue,
  V2Button,
  V2Card,
  V2CardContent,
  V2EnrollmentFlowMain,
  V2FormActions,
  V2Input,
  V2Select,
  V2SelectContent,
  V2SelectItem,
  V2SelectTrigger,
  V2SelectValue,
  V2Typography,
} from "@cakewalk/ui/v2";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import {
  US_STATES,
  businessComplete,
  formatEin,
  formatZip,
  toStateCode,
  validEin,
  validZip,
} from "./data";
import type { BusinessInfo, FlowMode, SubmitterInfo } from "./types";

/**
 * Step 2 of the company-census flow: confirm the business basics — name +
 * mailing address + phone (what an underwriter needs to scope a medical quote
 * by region).
 *
 * Two flow-specific behaviours:
 *  - logged-in clients land here pre-populated from their company profile (the
 *    orchestrator seeds `business`);
 *  - guests are not pre-populated and additionally supply their own name +
 *    email so we know who submitted the census.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function digitCount(value: string): number {
  return value.replace(/\D/g, "").length;
}

function formatPhoneProgressive(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/** Map a resolved Google place into the design-system address value shape. */
function placeDetailsToValue(d: PlaceDetails): V2AddressValue {
  return {
    formatted: d.formattedAddress,
    line1: d.addressLine1 ?? undefined,
    city: d.city ?? undefined,
    state: d.state ?? undefined,
    postalCode: d.postalCode ?? undefined,
    country: d.country ?? undefined,
    placeId: d.placeId,
  };
}

/** Debounce a fast-changing value (the address query) for the Places lookup. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

export function CompanyCensusBusinessStep({
  mode,
  business,
  submitter,
  playBuild = false,
  ceremony = null,
  onChange,
  onSubmitterChange,
  onBack,
  onNext,
}: {
  mode: FlowMode;
  business: BusinessInfo;
  submitter: SubmitterInfo;
  playBuild?: boolean;
  ceremony?: StepBuildCeremony | null;
  onChange: (next: BusinessInfo) => void;
  onSubmitterChange: (next: SubmitterInfo) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const isGuest = mode === "guest";

  const [addressResolved, setAddressResolved] = useState(() =>
    Boolean(business.city || business.state || business.zip),
  );
  const [touched, setTouched] = useState<
    Partial<Record<keyof BusinessInfo | "submitterName" | "submitterEmail", boolean>>
  >({});
  const markTouched = (field: keyof BusinessInfo | "submitterName" | "submitterEmail") => () =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const update = (patch: Partial<BusinessInfo>) => onChange({ ...business, ...patch });

  // Show the structured city/state/zip block whenever we have a resolved
  // address OR pre-filled location data. The logged-in seed arrives after this
  // step mounts, so the initial `addressResolved` value can't capture it.
  const showAddressDetails =
    addressResolved || Boolean(business.city || business.state || business.zip);

  const nameError = touched.name && !business.name.trim() ? "Enter your business name." : undefined;
  const einError =
    touched.ein && !validEin(business.ein)
      ? business.ein.trim().length === 0
        ? "Enter your company's EIN."
        : "EIN must be 9 digits (XX-XXXXXXX)."
      : undefined;
  // The address block only renders once an address is entered/resolved, so its
  // city/state/ZIP fields are required-and-shown together: once visible, an
  // empty one is an error (Places usually fills them; a partial or manual
  // address can leave a gap the employer must complete). Address, city, state,
  // and ZIP are all mandatory — only phone is optional.
  const addressError =
    touched.address && !business.address.trim() ? "Enter your business address." : undefined;
  // City/state/ZIP are required once the address block is shown, but the red
  // only appears after the field has been left (blurred) — never while typing.
  const cityError =
    touched.city && showAddressDetails && !business.city.trim() ? "Enter your city." : undefined;
  const stateError =
    touched.state && showAddressDetails && !business.state ? "Select your state." : undefined;
  const zipError =
    touched.zip && showAddressDetails
      ? business.zip.length === 0
        ? "Enter your ZIP code."
        : !validZip(business.zip)
          ? "ZIP must be 5 or 9 digits."
          : undefined
      : undefined;
  const phoneError =
    touched.phone && business.phone.length > 0 && digitCount(business.phone) !== 10
      ? "Enter a 10-digit phone number."
      : undefined;

  const submitterNameError =
    isGuest && touched.submitterName && !submitter.name.trim() ? "Enter your name." : undefined;
  const submitterEmailError =
    isGuest && touched.submitterEmail && !EMAIL_RE.test(submitter.email.trim())
      ? "Enter a valid email address."
      : undefined;

  const guestValid =
    !isGuest || (submitter.name.trim().length > 0 && EMAIL_RE.test(submitter.email.trim()));

  // Every business field is required except phone: name + a full mailing
  // address (street, city, state, 5-digit ZIP). Phone, if entered, must be a
  // complete 10-digit number.
  const canContinue =
    businessComplete(business) &&
    !phoneError &&
    guestValid &&
    (digitCount(business.phone) === 0 || digitCount(business.phone) === 10);

  const applyAddress = (value: V2AddressValue | undefined) => {
    if (!value) {
      setAddressResolved(false);
      update({ address: "", city: "", state: "", zip: "" });
      return;
    }
    setAddressResolved(true);
    update({
      address: value.line1 ?? value.formatted,
      city: value.city ?? business.city,
      // Places returns the full state name ("New York"); the state select keys
      // on the USPS code ("NY"), so normalize or the field stays blank.
      state: toStateCode(value.state) || business.state,
      zip: value.postalCode ?? business.zip,
    });
  };

  // Real Google Places autocomplete via the server-side `/api/places` proxy.
  // We only search while the user is actively typing a not-yet-resolved address
  // of at least 3 characters (avoids a lookup on the pre-filled value).
  const addressQuery = useDebouncedValue(business.address.trim(), 250);
  const autocomplete = useQuery({
    queryKey: ["places-autocomplete", addressQuery],
    queryFn: () => placesAutocomplete(addressQuery),
    enabled: !addressResolved && addressQuery.length >= 3,
    staleTime: 60_000,
  });

  const addressSuggestions: V2AddressSuggestion[] = useMemo(
    () =>
      (autocomplete.data ?? []).map((p) => ({
        id: p.placeId,
        placeId: p.placeId,
        primaryText: p.mainText,
        secondaryText: p.secondaryText,
      })),
    [autocomplete.data],
  );

  const [resolvingAddress, setResolvingAddress] = useState(false);
  const handleSuggestionSelect = (suggestion: V2AddressSuggestion) => {
    const placeId = suggestion.placeId ?? suggestion.id;
    if (!placeId) return;
    setResolvingAddress(true);
    placesDetails(placeId)
      .then((details) => applyAddress(placeDetailsToValue(details)))
      .catch(() =>
        // If details resolution fails, fall back to the suggestion text so the
        // user isn't blocked — they can still correct city/state/zip by hand.
        applyAddress({
          formatted: suggestion.secondaryText
            ? `${suggestion.primaryText}, ${suggestion.secondaryText}`
            : suggestion.primaryText,
          line1: suggestion.primaryText,
        }),
      )
      .finally(() => setResolvingAddress(false));
  };

  const { eyebrowMotion, titleMotion, descriptionMotion } = sectionPageIntroMotions(
    playBuild,
    ceremony,
  );

  return (
    <V2EnrollmentFlowMain width="default">
      <div className="mx-auto w-full max-w-cw-v2-content-narrow pb-8">
        <div className="mb-8 flex flex-col items-center gap-2 text-center">
          <V2Typography
            variant="overline"
            {...mergeStepBuildProps(eyebrowMotion, { className: "text-cw-v2-reliant-red" })}
          >
            Your business
          </V2Typography>
          <V2Typography variant="display" as="h1" {...mergeStepBuildProps(titleMotion)}>
            Confirm your business details
          </V2Typography>
          <V2Typography
            variant="body-large"
            muted
            {...mergeStepBuildProps(descriptionMotion, { className: "max-w-xl" })}
          >
            {isGuest
              ? "Tell us about your business and how to reach you, so we can prepare your medical quote."
              : "We\u2019ve got you on file for your current benefits — just confirm these are still right for your medical quote."}
          </V2Typography>
        </div>

        {isGuest ? (
          <V2Card
            {...mergeStepBuildProps(pageBodyBlockBuildProps(playBuild, 0, ceremony), {
              className: "mb-6 overflow-hidden",
            })}
          >
            <V2CardContent className="flex flex-col gap-6 p-6 sm:p-8">
              <div className="flex items-start gap-3 border-b border-cw-v2-border/60 pb-5">
                <span
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-cw-v2-md bg-cw-v2-coral-100 text-cw-v2-reliant-red"
                  aria-hidden="true"
                >
                  <User className="h-5 w-5" />
                </span>
                <div className="flex flex-col gap-0.5">
                  <V2Typography variant="body" className="font-semibold text-cw-v2-ink">
                    About you
                  </V2Typography>
                  <V2Typography variant="body-small" muted>
                    So we know who to follow up with about this quote.
                  </V2Typography>
                </div>
              </div>

              <div className="grid items-start gap-4 sm:grid-cols-2">
                <V2Input
                  label="Your name"
                  placeholder="Jordan Rivera"
                  value={submitter.name}
                  onChange={(e) => onSubmitterChange({ ...submitter, name: e.target.value })}
                  onBlur={markTouched("submitterName")}
                  error={submitterNameError}
                  prefix={<User className="h-4 w-4" aria-hidden />}
                  size="lg"
                  data-sensitive
                />
                <V2Input
                  label="Your email address"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="you@company.com"
                  value={submitter.email}
                  onChange={(e) => onSubmitterChange({ ...submitter, email: e.target.value })}
                  onBlur={markTouched("submitterEmail")}
                  error={submitterEmailError}
                  prefix={<Mail className="h-4 w-4" aria-hidden />}
                  size="lg"
                  data-sensitive
                />
              </div>
            </V2CardContent>
          </V2Card>
        ) : null}

        <V2Card
          {...mergeStepBuildProps(pageBodyBlockBuildProps(playBuild, isGuest ? 1 : 0, ceremony), {
            className: "overflow-hidden",
          })}
        >
          <V2CardContent className="flex flex-col gap-6 p-6 sm:p-8">
            <div className="flex items-start gap-3 border-b border-cw-v2-border/60 pb-5">
              <span
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-cw-v2-md bg-cw-v2-blue-100 text-cw-v2-blue-600"
                aria-hidden="true"
              >
                <Briefcase className="h-5 w-5" />
              </span>
              <div className="flex flex-col gap-0.5">
                <V2Typography variant="body" className="font-semibold text-cw-v2-ink">
                  Business details
                </V2Typography>
                <V2Typography variant="body-small" muted>
                  We use your address to find the carriers and networks available in your area.
                </V2Typography>
              </div>
            </div>

            <V2Input
              label="Business name"
              placeholder="Acme Coffee Roasters"
              value={business.name}
              onChange={(e) => update({ name: e.target.value })}
              onBlur={markTouched("name")}
              error={nameError}
              prefix={<Store className="h-4 w-4" aria-hidden />}
              size="lg"
            />

            <V2Input
              label="Company EIN"
              placeholder="12-3456789"
              value={business.ein}
              onChange={(e) => update({ ein: formatEin(e.target.value) })}
              onBlur={markTouched("ein")}
              error={einError}
              prefix={<Hash className="h-4 w-4" aria-hidden />}
              inputMode="numeric"
              maxLength={10}
              size="lg"
              data-sensitive
            />

            <V2AddressAutocomplete
              id="company-census-address"
              label="Business address"
              placeholder="Start typing your street address"
              size="lg"
              query={business.address}
              onQueryChange={(address) => {
                if (addressResolved) setAddressResolved(false);
                setTouched((prev) => ({ ...prev, address: true }));
                update({ address });
              }}
              suggestions={addressSuggestions}
              onSuggestionSelect={handleSuggestionSelect}
              onValueChange={applyAddress}
              isLoading={autocomplete.isFetching || resolvingAddress}
              loadingText="Searching addresses…"
              showLine2={false}
              error={addressError}
              emptyText="Keep typing to find a matching address, or enter the full address manually."
              data-sensitive
            />

            <AnimatePresence initial={false}>
              {showAddressDetails ? (
                <motion.div
                  key="address-detail"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden"
                >
                  <div className="grid items-start gap-4 md:grid-cols-3">
                    <V2Input
                      label="City"
                      placeholder="San Francisco"
                      value={business.city}
                      onChange={(e) => update({ city: e.target.value })}
                      onBlur={markTouched("city")}
                      error={cityError}
                      size="lg"
                    />
                    <div className="cw-v2-input-field">
                      <V2Select
                        value={business.state || undefined}
                        onValueChange={(state) => {
                          markTouched("state")();
                          update({ state });
                        }}
                      >
                        <V2SelectTrigger
                          id="company-census-state"
                          label="State"
                          size="lg"
                          aria-label="State"
                          error={stateError}
                        >
                          <V2SelectValue placeholder="Select a state" />
                        </V2SelectTrigger>
                        <V2SelectContent>
                          {US_STATES.map((state) => (
                            <V2SelectItem key={state.value} value={state.value}>
                              {state.label}
                            </V2SelectItem>
                          ))}
                        </V2SelectContent>
                      </V2Select>
                    </div>
                    <V2Input
                      label="ZIP"
                      placeholder="94103"
                      value={business.zip}
                      onChange={(e) => update({ zip: formatZip(e.target.value) })}
                      onBlur={markTouched("zip")}
                      error={zipError}
                      inputMode="numeric"
                      maxLength={10}
                      size="lg"
                    />
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>

            <V2Input
              label="Phone"
              optional
              type="tel"
              placeholder="(555) 123-4567"
              value={formatPhoneProgressive(business.phone)}
              onChange={(e) => update({ phone: formatPhoneProgressive(e.target.value) })}
              onBlur={markTouched("phone")}
              error={phoneError}
              prefix={<Phone className="h-4 w-4" aria-hidden />}
              inputMode="tel"
              maxLength={14}
              size="lg"
            />
          </V2CardContent>
        </V2Card>

        <V2FormActions
          variant="registration"
          {...mergeStepBuildProps(pageBodyBlockBuildProps(playBuild, isGuest ? 2 : 1, ceremony), {
            className: "mt-8",
          })}
        >
          <V2Button
            variant="primary"
            size="lg"
            fullWidth
            disabled={!canContinue}
            onClick={onNext}
            iconSuffix={<ArrowRight className="h-4 w-4" />}
            data-track="button-company-census-business-continue"
          >
            Continue to your team
          </V2Button>
          <V2Button
            variant="link"
            onClick={onBack}
            data-track="button-company-census-business-back"
          >
            Back
          </V2Button>
        </V2FormActions>
      </div>
    </V2EnrollmentFlowMain>
  );
}
